import { useState, useEffect, useCallback } from 'react';
import { ProcessingJob, JobStatus, AudioFile, ProcessingConfig, ProcessedFile } from '../types';
import ApiService from '../services/api';
import PaymentsService from '../services/payments';

export const useProcessingJob = () => {
  const [currentJob, setCurrentJob] = useState<ProcessingJob | null>(null);
  const [jobHistory, setJobHistory] = useState<ProcessingJob[]>([]);
  const [isPolling, setIsPolling] = useState(false);

  // Poll job status every 3 seconds when processing (longer interval for mobile reliability)
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    let consecutiveFailures = 0;
    const MAX_CONSECUTIVE_FAILURES = 3; // Allow 3 consecutive failures before showing error (each with built-in retries)

    if (isPolling && currentJob?.id) {
      intervalId = setInterval(async () => {
        try {
          // ApiService.getJobStatus now has built-in retry logic (3 attempts with exponential backoff)
          // It will automatically retry on timeout/network errors before throwing
          const status = await ApiService.getJobStatus(currentJob.id);

          // Reset failure counter on successful status check
          consecutiveFailures = 0;

          setCurrentJob(prev => prev ? {
            ...prev,
            status: status.status,
            progress: status.progress,
            message: status.message,
            resultFiles: status.resultFiles,
            error: status.error,
            completedAt: status.status === 'completed' ? new Date() : prev.completedAt,
          } : null);

          // Stop polling if job is complete or failed
          if (status.status === 'completed' || status.status === 'error' || status.status === 'cancelled') {
            setIsPolling(false);

            // Invalidate credits cache when job completes (credits were deducted)
            if (status.status === 'completed') {
              PaymentsService.invalidateCache();
            }
          }
        } catch (error) {
          // Only reach here if all retries failed (after 3 attempts per status check)
          consecutiveFailures++;
          console.error(`Status check failed (${consecutiveFailures}/${MAX_CONSECUTIVE_FAILURES}):`, error);

          // Only show error after multiple consecutive poll failures
          if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
            setCurrentJob(prev => prev ? {
              ...prev,
              status: 'error',
              error: 'Unable to check job status. Please check your network connection.',
            } : null);
            setIsPolling(false);
          } else {
            // Continue polling - job might still be processing
            console.warn('Will retry status check on next poll interval');
          }
        }
      }, 3000); // 3 second interval (was 2 seconds)
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPolling, currentJob?.id]);

  const startProcessing = useCallback(async (
    targetFile: AudioFile,
    referenceFile: AudioFile,
    config: ProcessingConfig,
    referenceFile2?: AudioFile | null,
    referenceBlend?: number
  ): Promise<void> => {
    try {
      // Upload target file
      setCurrentJob({
        id: 'temp',
        status: 'uploading',
        progress: 0,
        message: 'Uploading target file...',
        targetFile,
        referenceFile,
        referenceFile2: referenceFile2 || undefined,
        referenceBlend,
        config,
        createdAt: new Date(),
      });

      const targetUpload = await ApiService.uploadTarget(
        targetFile.file,
        (progress) => {
          setCurrentJob(prev => prev ? {
            ...prev,
            progress: progress / 3,
            message: `Uploading target file... ${progress}% (may take 1-2 minutes on slower connections)`
          } : null);
        }
      );

      // Upload reference file 1
      setCurrentJob(prev => prev ? {
        ...prev,
        message: 'Uploading reference file 1... (may take 1-2 minutes on slower connections)',
        progress: 33,
      } : null);

      const referenceUpload = await ApiService.uploadReference(
        referenceFile.file,
        (progress) => {
          setCurrentJob(prev => prev ? {
            ...prev,
            progress: 33 + (progress / 3),
            message: `Uploading reference file 1... ${progress}%`
          } : null);
        }
      );

      // Upload reference file 2 if provided
      let referenceUpload2;
      if (referenceFile2) {
        setCurrentJob(prev => prev ? {
          ...prev,
          message: 'Uploading reference file 2... (may take 1-2 minutes on slower connections)',
          progress: 66,
        } : null);

        referenceUpload2 = await ApiService.uploadReference(
          referenceFile2.file,
          (progress) => {
            setCurrentJob(prev => prev ? {
              ...prev,
              progress: 66 + (progress / 3),
              message: `Uploading reference file 2... ${progress}%`
            } : null);
          }
        );
      }

      // Start processing
      setCurrentJob(prev => prev ? {
        ...prev,
        message: 'Starting processing...',
        progress: 100,
      } : null);

      const processResponse = await ApiService.startProcessing(
        targetUpload.fileId,
        referenceUpload.fileId,
        config,
        referenceUpload2?.fileId,
        referenceBlend
      );

      const newJob: ProcessingJob = {
        id: processResponse.jobId,
        status: processResponse.status,
        progress: 0,
        message: processResponse.message,
        targetFile,
        referenceFile,
        referenceFile2: referenceFile2 || undefined,
        referenceBlend,
        config,
        createdAt: new Date(),
      };

      setCurrentJob(newJob);
      setIsPolling(true);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setCurrentJob(prev => prev ? {
        ...prev,
        status: 'error',
        error: errorMessage,
      } : null);
    }
  }, []);

  const cancelProcessing = useCallback(async (): Promise<void> => {
    if (!currentJob?.id || currentJob.id === 'temp') return;

    try {
      await ApiService.cancelJob(currentJob.id);
      setCurrentJob(prev => prev ? {
        ...prev,
        status: 'cancelled',
        message: 'Processing cancelled',
      } : null);
      setIsPolling(false);
    } catch (error) {
      console.error('Error cancelling job:', error);
    }
  }, [currentJob?.id]);

  const downloadFile = useCallback(async (file: ProcessedFile): Promise<void> => {
    if (!currentJob?.id) return;

    try {
      const blob = await ApiService.downloadFile(currentJob.id, file.filename);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  }, [currentJob?.id]);

  const clearCurrentJob = useCallback(() => {
    if (currentJob) {
      setJobHistory(prev => [...prev, currentJob]);
    }
    setCurrentJob(null);
    setIsPolling(false);
  }, [currentJob]);

  const runAnalysis = useCallback(async (): Promise<void> => {
    if (!currentJob?.id) return;

    try {
      // Call the analysis endpoint
      await ApiService.runAnalysis(currentJob.id);

      // Start polling to get updated job status with analysis results
      setIsPolling(true);
    } catch (error) {
      console.error('Error starting analysis:', error);
      throw error;
    }
  }, [currentJob?.id]);

  return {
    currentJob,
    jobHistory,
    isProcessing: isPolling,
    startProcessing,
    cancelProcessing,
    downloadFile,
    clearCurrentJob,
    runAnalysis,
  };
};